import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { adminAPI } from '../../../services/authService';
import { confirmDialog } from '../../../utils/confirmDialog';
import { notifyError, notifySuccess } from '../../../utils/notify';

const UsersAccessProjectAccess = () => {
  const [view, setView] = useState('project');
  const [assignments, setAssignments] = useState([]);
  const [users, setUsers] = useState([]);
  const [projects, setProjects] = useState([]);
  const [projectId, setProjectId] = useState('');
  const [userId, setUserId] = useState('');
  const [selectedUserIds, setSelectedUserIds] = useState([]);
  const [responsibility, setResponsibility] = useState('SITE');
  const [isPrimary, setIsPrimary] = useState(false);

  const load = useCallback(async () => {
    const [a, u, p] = await Promise.all([
      adminAPI.getAssignments(),
      adminAPI.getUsers(),
      adminAPI.getProjects(),
    ]);
    setAssignments(a?.data?.data || a?.data || []);
    setUsers(u?.data?.data || u?.data || []);
    setProjects(p?.data?.data || p?.data || []);
  }, []);

  useEffect(() => { load().catch(() => notifyError('Unable to load project access')); }, [load]);

  const byProject = useMemo(() => {
    const map = new Map();
    assignments.forEach((a) => {
      const pid = a.projectId || a.project?.id;
      if (!map.has(pid)) map.set(pid, []);
      map.get(pid).push(a);
    });
    return map;
  }, [assignments]);

  const assignToProject = async () => {
    if (!projectId || selectedUserIds.length === 0) {
      notifyError('Select a project and at least one user');
      return;
    }
    try {
      const pid = Number(projectId);
      const existingRows = byProject.get(pid) || [];
      for (const uidRaw of selectedUserIds) {
        const uid = Number(uidRaw);
        const existing = existingRows.find((a) => Number(a.userId) === uid);
        if (existing) {
          await adminAPI.updateAssignment(existing.id, {
            role: responsibility,
            isPrimary,
          });
        } else {
          await adminAPI.createAssignment({
            userId: uid,
            projectId: pid,
            role: responsibility,
            isPrimary,
          });
        }
      }
      notifySuccess('Project access updated');
      setSelectedUserIds([]);
      await load();
    } catch (err) {
      notifyError(err?.response?.data?.message || 'Assign failed');
    }
  };

  const assignToUser = async () => {
    if (!userId || !projectId) {
      notifyError('Select user and project');
      return;
    }
    try {
      const uid = Number(userId);
      const pid = Number(projectId);
      const existing = assignments.find(
        (a) => Number(a.userId) === uid && Number(a.projectId) === pid,
      );
      if (existing) {
        await adminAPI.updateAssignment(existing.id, {
          role: responsibility,
          isPrimary,
        });
        notifySuccess('Project access updated');
      } else {
        await adminAPI.createAssignment({
          userId: uid,
          projectId: pid,
          role: responsibility,
          isPrimary,
        });
        notifySuccess('Project access added');
      }
      await load();
    } catch (err) {
      notifyError(err?.response?.data?.message || 'Assign failed');
    }
  };

  const removeAssignment = async (id) => {
    if (!(await confirmDialog('Remove this project access?', 'Remove Access'))) return;
    try {
      await adminAPI.deleteAssignment(id);
      notifySuccess('Removed');
      await load();
    } catch (err) {
      notifyError(err?.response?.data?.message || 'Remove failed');
    }
  };

  return (
    <div>
      <div className="filter-bar">
        <button type="button" className={`ua-tab ${view === 'project' ? 'is-active' : ''}`} onClick={() => setView('project')}>By Project</button>
        <button type="button" className={`ua-tab ${view === 'user' ? 'is-active' : ''}`} onClick={() => setView('user')}>By User</button>
      </div>

      <div className="panel ua-panel" style={{ marginBottom: 16 }}>
        <h3 style={{ marginTop: 0 }}>{view === 'project' ? 'Assign users to project' : 'Assign project to user'}</h3>
        <div className="filter-bar" style={{ marginBottom: 0 }}>
          <select value={projectId} onChange={(e) => setProjectId(e.target.value)}>
            <option value="">Select project</option>
            {projects.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select>
          {view === 'user' && (
            <select value={userId} onChange={(e) => setUserId(e.target.value)}>
              <option value="">Select user</option>
              {users.map((u) => <option key={u.id} value={u.id}>{u.name} ({u.email})</option>)}
            </select>
          )}
          {view === 'project' && (
            <select
              multiple
              value={selectedUserIds}
              onChange={(e) => setSelectedUserIds(Array.from(e.target.selectedOptions).map((o) => o.value))}
              style={{ minHeight: 90, minWidth: 220 }}
            >
              {users.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
            </select>
          )}
          <select value={responsibility} onChange={(e) => setResponsibility(e.target.value)} title="Project Responsibility">
            <option value="SITE">SITE</option>
            <option value="VIEWER">VIEWER</option>
            <option value="PM">PM</option>
            <option value="HO">HO</option>
          </select>
          <label style={{ display: 'inline-flex', gap: 6, alignItems: 'center', fontSize: 13 }}>
            <input type="checkbox" checked={isPrimary} onChange={(e) => setIsPrimary(e.target.checked)} />
            Primary project
          </label>
          <button type="button" className="btn-primary" onClick={view === 'project' ? assignToProject : assignToUser}>Assign</button>
        </div>
        <p style={{ fontSize: 12, color: '#64748b', margin: '8px 0 0' }}>Project Responsibility is separate from System Role.</p>
      </div>

      <div className="table-container">
        <table className="data-table">
          <thead>
            <tr>
              {view === 'project' ? <><th>Project</th><th>User</th></> : <><th>User</th><th>Project</th></>}
              <th>Project Responsibility</th>
              <th>Primary</th>
              <th>Access dates</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {assignments.length === 0 ? (
              <tr><td colSpan={6} style={{ textAlign: 'center', color: '#64748b', padding: 20 }}>No assignments</td></tr>
            ) : assignments.map((a) => (
              <tr key={a.id}>
                {view === 'project' ? (
                  <>
                    <td>{a.project?.name || a.projectId}</td>
                    <td>{a.user?.name || a.userId}</td>
                  </>
                ) : (
                  <>
                    <td>{a.user?.name || a.userId}</td>
                    <td>{a.project?.name || a.projectId}</td>
                  </>
                )}
                <td>{a.role || '—'}</td>
                <td>{a.isPrimary ? 'Yes' : '—'}</td>
                <td>
                  {[a.accessStartDate, a.accessEndDate].filter(Boolean).map((d) => new Date(d).toLocaleDateString('en-IN')).join(' → ') || '—'}
                </td>
                <td><button type="button" className="btn-secondary btn-sm" onClick={() => removeAssignment(a.id)}>Remove</button></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default UsersAccessProjectAccess;
